import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Calculator, Sun, Moon, Sparkles, Heart, History, Shield, Database, ArrowUp } from 'lucide-react';
import AdSpace from '../components/AdSpace';
import AuthorBio, { ContentDisclaimer } from '../components/AuthorBio';
import { useSetMeta } from '../hooks/useSetMeta';

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQCategory {
    id: string;
    title: string;
    icon: React.ReactNode;
    description: string;
    faqs: FAQItem[];
}

const FAQ_CATEGORIES: FAQCategory[] = [
    {
        id: 'general',
        title: 'General Questions',
        icon: <HelpCircle size={24} className="text-primary" />,
        description: 'Common questions about ZodiAge and how it works.',
        faqs: [
            {
                question: "Is astrology scientifically proven?",
                answer: "Astrology is not a science in the modern empirical sense. It is a symbolic system developed over millennia, used for self-reflection, personal insight, and cultural tradition. Scientific studies have not validated astrology's predictive claims. We present astrological content as a framework for introspection—not as factual prediction. Treat readings as prompts for self-reflection, not as deterministic forecasts."
            },
            {
                question: "Is my data secure?",
                answer: "Yes. All your data (birth date, settings, reading history) is stored locally in your browser using localStorage. Nothing is uploaded to external servers. We do not collect, store, or sell personal information. To remove all data, simply clear your browser's localStorage for this site."
            },
            {
                question: "Why do some features have cooldowns?",
                answer: "Daily cooldowns (24 hours) encourage thoughtful engagement rather than compulsive use. For features like tarot, compatibility checks, and personality profiles, the cooldown gives you time to reflect on your reading before generating a new one. Premium users can bypass cooldowns for unrestricted access."
            },
            {
                question: "How accurate are these calculations?",
                answer: "Our age calculations are mathematically precise, accounting for leap years, varying month lengths, and timezone differences. Zodiac sign assignments follow standard astrological date ranges. AI-generated content (personality profiles, compatibility readings) is created by language models and should be viewed as creative interpretation, not definitive analysis."
            },
            {
                question: "Can I use ZodiAge on mobile devices?",
                answer: "Yes. The site is fully responsive and works on smartphones and tablets. All features function on mobile browsers. For the best experience with the 3D zodiac wheel, we recommend landscape orientation on tablets."
            },
            {
                question: "Why did I get different results than I expected?",
                answer: "Several factors can affect results: (1) Birth time affects Rising sign calculations—without exact time, results are estimated. (2) Chinese zodiac depends on the lunar calendar; January/February births may belong to the previous year's animal. (3) AI-generated content varies with each generation. If something seems wrong, double-check your input data."
            }
        ]
    },
    {
        id: 'age',
        title: 'Age Calculator',
        icon: <Calculator size={24} className="text-yellow-400" />,
        description: 'Questions about age calculation and time units.',
        faqs: [
            {
                question: "How is my exact age calculated?",
                answer: "We calculate the difference between your birth date/time and the current moment. The result is broken down into years, months, days, hours, minutes, and seconds. Leap years (including the century rule: divisible by 400 is a leap year) and varying month lengths are handled correctly."
            },
            {
                question: "What is East Asian age reckoning?",
                answer: "In many East Asian cultures, a person is considered one year old at birth, and everyone gains a year on New Year's Day rather than their birthday. This means someone born on December 31 could be 'two years old' by January 1. Our calculator uses Western (international) counting by default."
            },
            {
                question: "Why does my age show decimals?",
                answer: "Decimal age shows your exact age as a single number (e.g., 34.567 years). This accounts for the fractional year you've lived since your last birthday. It's useful for precise calculations like life progress percentages."
            },
            {
                question: "What if I was born on February 29?",
                answer: "Leap day babies have a birthday that only appears on the calendar every four years. Our calculator handles this correctly by counting actual days elapsed, so leap-day babies get accurate results regardless of whether the current year is a leap year."
            }
        ]
    },
    {
        id: 'western',
        title: 'Western Zodiac',
        icon: <Sun size={24} className="text-orange-400" />,
        description: 'Questions about Sun, Moon, Rising signs and compatibility.',
        faqs: [
            {
                question: "What are the 'Big Three' in astrology?",
                answer: "The Big Three refers to your Sun sign (core identity), Moon sign (emotional nature), and Rising sign (outward persona/first impression). Together, they provide a more complete picture of your astrological profile than the Sun sign alone."
            },
            {
                question: "Why is birth time important for the Rising sign?",
                answer: "The Rising sign (Ascendant) is the zodiac sign rising on the eastern horizon at your exact moment of birth. It changes approximately every two hours. Without knowing your birth time, the Rising sign can only be estimated. Check your birth certificate or ask family members for accurate time."
            },
            {
                question: "How is the Moon sign calculated?",
                answer: "The Moon moves through each zodiac sign approximately every 2.5 days. Your Moon sign is determined by the Moon's position on your birth date. Our calculator provides an estimate based on date; for precise accuracy, professional astrology software uses birth time and location."
            },
            {
                question: "What if I'm born 'on the cusp'?",
                answer: "Being born on the cusp means your birthday falls within a day or two of the Sun moving from one sign to another. Technically, the Sun is always in exactly one sign at any moment. While pop astrology suggests cusp-borns share traits of both signs, traditional astrology emphasizes your actual Sun sign."
            },
            {
                question: "Can incompatible signs have good relationships?",
                answer: "Absolutely. 'Incompatible' in astrology usually means 'requires more effort' or 'creates productive tension.' Many successful relationships pair signs that seem mismatched on paper. Compatibility readings are conversation starters and reflection tools, not relationship verdicts."
            },
            {
                question: "What are the Four Elements?",
                answer: "Fire (Aries, Leo, Sagittarius): Passionate, dynamic, spontaneous. Earth (Taurus, Virgo, Capricorn): Practical, grounded, reliable. Air (Gemini, Libra, Aquarius): Intellectual, communicative, social. Water (Cancer, Scorpio, Pisces): Emotional, intuitive, empathetic. Signs of the same element typically harmonize well."
            }
        ]
    },
    {
        id: 'chinese',
        title: 'Chinese Zodiac',
        icon: <Moon size={24} className="text-red-400" />,
        description: 'Questions about animals, elements, and lunar calendar.',
        faqs: [
            {
                question: "Why might my Chinese zodiac sign differ from what I expected?",
                answer: "The Chinese zodiac follows the lunar calendar. Chinese New Year falls between late January and mid-February. If you were born in January or early February, your animal sign might belong to the previous year. Always check the exact Lunar New Year date for your birth year."
            },
            {
                question: "What are the Five Elements?",
                answer: "Chinese astrology incorporates five elements—Wood, Fire, Earth, Metal, and Water—each cycling every two years. Combined with the 12 animals, this creates a 60-year cycle. Your element modifies your animal's expression: a Water Tiger is more intuitive; a Fire Tiger is more bold."
            },
            {
                question: "What do Yin and Yang mean for my sign?",
                answer: "Yin animals (Ox, Rabbit, Snake, Goat, Rooster, Pig) are traditionally associated with receptive, introspective energy. Yang animals (Rat, Tiger, Dragon, Horse, Monkey, Dog) are associated with active, outgoing energy. This adds another layer to your animal's personality description."
            },
            {
                question: "Is the Dragon really the luckiest sign?",
                answer: "In Chinese culture, the Dragon is highly auspicious, associated with power, luck, and imperial authority. Birth rates often rise in Dragon years. However, every animal has unique strengths. 'Luckiest' is subjective and context-dependent."
            },
            {
                question: "How is Chinese zodiac different from Western zodiac?",
                answer: "Western astrology assigns signs by birth month (based on the Sun's position). Chinese astrology assigns signs by birth year (based on a 12-year lunar cycle). A person can be a Leo (Western) and a Rooster (Chinese) simultaneously—these are completely separate systems."
            }
        ]
    },
    {
        id: 'tarot',
        title: 'Tarot',
        icon: <Sparkles size={24} className="text-purple-400" />,
        description: 'Questions about card readings and interpretation.',
        faqs: [
            {
                question: "Can tarot predict my future?",
                answer: "Tarot is not fortune-telling. It's a reflection tool that helps you examine your thoughts, feelings, and options. The cards don't control outcomes—you do. Use readings to clarify your own intuition, not to seek prophecy. Cards offer themes to contemplate, not predictions to expect."
            },
            {
                question: "What does 'reversed' mean?",
                answer: "A reversed card appears upside-down when drawn. Reversed meanings aren't simply 'bad'—they often indicate delay, internalization, blockage, or an underdeveloped aspect of the card's theme. For example, Strength reversed might suggest self-doubt rather than cruelty."
            },
            {
                question: "How is my daily card chosen?",
                answer: "The card is selected randomly from the 22 Major Arcana using a cryptographically random number generator. There's no human bias or pre-selection. The randomness mirrors the shuffle of a physical deck. The card may appear upright or reversed with equal probability."
            },
            {
                question: "Why do I keep drawing the same card?",
                answer: "Statistically, drawing the same card consecutively is uncommon but possible. Symbolically, repeated cards often indicate a theme you haven't fully addressed. The lesson remains relevant. Journal about what the card might be asking you to acknowledge or change."
            },
            {
                question: "What's the difference between Major and Minor Arcana?",
                answer: "The 22 Major Arcana (The Fool through The World) represent significant life themes, karmic lessons, and archetypal forces. The 56 Minor Arcana (Wands, Cups, Swords, Pentacles) represent day-to-day events and situations. Our daily card feature uses Major Arcana only."
            },
            {
                question: "Is there a 'bad' card in tarot?",
                answer: "No card is inherently bad. Cards like Death or The Tower often alarm beginners, but they represent transformation and necessary change—endings that make room for new beginnings. Every card has constructive guidance if approached with openness."
            }
        ]
    },
    {
        id: 'life',
        title: 'Life Progress & Timeline',
        icon: <Heart size={24} className="text-green-400" />,
        description: 'Questions about life expectancy and milestones.',
        faqs: [
            {
                question: "Is Life Progress predicting when I will die?",
                answer: "No. Life Progress is a reflective visualization based on statistical life expectancy, not a personal prediction. Life expectancy is an average, and your individual lifespan depends on countless factors no calculator can know. Use it for philosophical reflection, not anxiety."
            },
            {
                question: "Where does the life expectancy number come from?",
                answer: "The default (approximately 73 years) is based on the World Health Organization's global average. However, this varies significantly by country, gender, lifestyle, and healthcare access. The adjustable slider lets you customize based on your own situation and expectations."
            },
            {
                question: "What is the 'Solar Cycle' visualization?",
                answer: "It's a poetic metaphor treating your entire life as a single symbolic day. Birth is sunrise, death is sunset, and your current age maps to a time between them. It encourages reflection: 'What time of day is it in my life?'"
            },
            {
                question: "What are astrological life milestones?",
                answer: "Certain ages correspond to significant planetary cycles. The Saturn Return (~29-30) marks a transition to full adulthood. The Uranus Opposition (~42) is associated with mid-life awakening. The Chiron Return (~50) relates to healing and wisdom. These are descriptive frameworks, not prescriptive rules."
            }
        ]
    },
    {
        id: 'technical',
        title: 'Technical & Privacy',
        icon: <Shield size={24} className="text-cyan-400" />,
        description: 'Questions about data, privacy, and technical details.',
        faqs: [
            {
                question: "Where is my data stored?",
                answer: "All data is stored in your browser's localStorage under keys 'zodiage_user' and 'zodiage_settings_v1'. This is local to your device—we never upload your birth date or readings to any server. To clear data, use your browser's 'Clear Site Data' function or delete localStorage entries manually."
            },
            {
                question: "Do you use cookies?",
                answer: "We use minimal cookies required for essential site functionality. Third-party ad services (like Google AdSense) may use cookies for ad personalization. You can manage cookie preferences through your browser settings."
            },
            {
                question: "What AI model generates content?",
                answer: "AI-generated content (personality profiles, compatibility readings, cosmic insights) uses Google's Gemini language model. Results vary between generations. AI content is creative interpretation based on astrological themes, not factual analysis."
            },
            {
                question: "Why do some features require waiting?",
                answer: "Cooldowns serve two purposes: (1) Encouraging reflection rather than rapid re-generation. (2) Managing API costs for AI-generated content. Premium subscriptions remove cooldowns for users who want unrestricted access."
            }
        ]
    }
];

const FAQSection: React.FC<{ category: FAQCategory }> = ({ category }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    return (
        <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
                <div className="bg-cosmic-700/50 p-3 rounded-xl">
                    {category.icon}
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">{category.title}</h3>
                    <p className="text-gray-400 text-sm">{category.description}</p>
                </div>
            </div>
            <div className="space-y-2">
                {category.faqs.map((faq, index) => (
                    <div 
                        key={index}
                        className="bg-cosmic-800/30 border border-cosmic-700/50 rounded-xl overflow-hidden"
                    >
                        <button
                            onClick={() => setOpenIndex(openIndex === index ? null : index)}
                            className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-cosmic-700/20 transition-colors"
                        >
                            <span className="font-medium text-white pr-4">{faq.question}</span>
                            {openIndex === index ? (
                                <ChevronUp size={18} className="text-gray-400 flex-shrink-0" />
                            ) : (
                                <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
                            )}
                        </button>
                        {openIndex === index && (
                            <div className="px-5 pb-4 pt-0 text-gray-300 text-sm leading-relaxed animate-in fade-in slide-in-from-top-1 duration-200">
                                {faq.answer}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};

const FAQPage: React.FC = () => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'FAQ - Frequently Asked Questions | ZodiAge',
    description: 'Get answers to common questions about astrology, zodiac signs, tarot readings, birth charts, and how to use ZodiAge tools.',
    ogImage: 'https://zodiage.com/logo.png'
  });
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    
    const filteredCategories = activeCategory 
        ? FAQ_CATEGORIES.filter(c => c.id === activeCategory)
        : FAQ_CATEGORIES;

    return (
        <div className="space-y-12 animate-in fade-in duration-500 pb-12">
            {/* Header */}
            <article className="max-w-4xl mx-auto">
                <header className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                        Find answers to common questions about ZodiAge, astrology, tarot, and how our tools work.
                        Can't find what you're looking for? Check our Learning Center for detailed guides.
                    </p>
                </header>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                    <button
                        onClick={() => setActiveCategory(null)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            activeCategory === null 
                                ? 'bg-primary text-white' 
                                : 'bg-cosmic-800/50 text-gray-300 hover:bg-cosmic-700/50'
                        }`}
                    >
                        All Topics
                    </button>
                    {FAQ_CATEGORIES.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                activeCategory === cat.id 
                                    ? 'bg-primary text-white' 
                                    : 'bg-cosmic-800/50 text-gray-300 hover:bg-cosmic-700/50'
                            }`}
                        >
                            {cat.title}
                        </button>
                    ))}
                </div>
            </article>

            {/* FAQ Sections */}
            <div className="max-w-4xl mx-auto px-4">
                {filteredCategories.map(category => (
                    <FAQSection key={category.id} category={category} />
                ))}
            </div>

            {/* Still Have Questions */}
            <div className="max-w-4xl mx-auto px-4">
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 text-center">
                    <h2 className="text-xl font-bold text-white mb-3">Still Have Questions?</h2>
                    <p className="text-gray-300 text-sm mb-4">
                        Explore our Learning Center for detailed educational content, or visit specific tool pages 
                        for feature-specific guidance.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href="/resources" className="bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-full text-sm font-medium transition-colors">
                            Learning Center
                        </a>
                        <a href="/guide" className="bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">
                            Getting Started Guide
                        </a>
                        <a href="/glossary" className="bg-purple-400/20 hover:bg-purple-400/30 text-purple-400 px-4 py-2 rounded-full text-sm font-medium transition-colors">
                            Glossary
                        </a>
                    </div>
                </section>
            </div>

            {/* Author Bio - E-E-A-T Signal */}
            <div className="max-w-4xl mx-auto px-4 mt-8">
                <AuthorBio variant="compact" topic="frequently asked questions and user support" />
            </div>

            {/* Content Disclaimer */}
            <div className="max-w-4xl mx-auto px-4 mt-4">
                <ContentDisclaimer variant="compact" />
            </div>

            {/* Back to Top Button */}
            <div className="flex justify-center mt-8">
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105 hover:shadow-purple-600/50"
                >
                    <ArrowUp size={18} />
                    <span>Back to Top</span>
                </button>
            </div>

            {/* Multiplex Ad for FAQ Page */}
            <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
        </div>
    );
};

export default FAQPage;
